import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, X, ArrowLeftRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `${Math.round(n).toLocaleString("fr-FR")} €`;
const CHOICES = [
  { k: "yes", l: "Oui, disponible", d: "Au tarif annoncé", Icon: Check, on: "border-[#4F6B4A] bg-[#EEF3EC] text-[#4F6B4A]" },
  { k: "counter", l: "Contre-proposer", d: "Un autre tarif", Icon: ArrowLeftRight, on: "border-[#8C6C50] bg-[#F5EFE6] text-[#6B5B49]" },
  { k: "no", l: "Non", d: "Pas disponible", Icon: X, on: "border-[#8A3B32] bg-[#F7EDEC] text-[#8A3B32]" },
];

export function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F5F3ED] px-4 py-10 sm:py-16">
      <div className="max-w-2xl mx-auto">
        <div className="font-serif text-2xl text-[#2B3029] tracking-tight">matchappart</div>
        <div className="mt-6 bg-white border border-[#E5E1D8] rounded-md p-6 sm:p-8">{children}</div>
      </div>
    </div>
  );
}

function Announced({ a }) {
  const parts = [a.month != null && `${eur(a.month)} / mois`, a.night != null && `${eur(a.night)} / nuit`].filter(Boolean);
  return <span data-testid="announced-price">{parts.length ? parts.join(" · ") : "non renseigné"}</span>;
}

function PriceInput({ label, value, onChange, testid }) {
  return (
    <label className="block flex-1">
      <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#666]">{label}</span>
      <div className="mt-2 flex items-center gap-2">
        <input type="number" min="1" step="1" inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-[#F5F3ED] border border-[#E5E1D8] rounded-md text-sm focus:outline-none focus:border-[#8C6C50]" data-testid={testid} />
        <span className="text-sm text-[#666]">€</span>
      </div>
    </label>
  );
}

function Choices({ answer, setAnswer }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
      {CHOICES.map(({ k, l, d, Icon, on }) => (
        <button key={k} type="button" onClick={() => setAnswer(k)} className={`text-left border-2 rounded-md p-4 transition-colors ${answer === k ? on : "border-[#E5E1D8] hover:border-[#8C6C50] text-[#1A1A1A]"}`} data-testid={`residence-choice-${k}`}>
          <Icon className="w-5 h-5" />
          <div className="font-medium mt-2">{l}</div>
          <div className="text-xs opacity-80">{d}</div>
        </button>
      ))}
    </div>
  );
}

export default function ResidenceResponse() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [answer, setAnswer] = useState(["yes", "no", "counter"].includes(params.get("a")) ? params.get("a") : "");
  const [month, setMonth] = useState("");
  const [night, setNight] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/residence-response/${token}`)
      .then(({ data }) => {
        setInfo(data);
        const c = data.current;
        if (!c) return;
        setAnswer((a) => a || c.answer);
        if (c.answer === "counter") {
          setMonth(c.unit === "month" && c.price != null ? String(c.price) : "");
          setNight(c.price_night != null ? String(c.price_night) : "");
        }
      })
      .catch((e) => setError(e.response?.data?.detail || "Lien invalide"));
  }, [token]);

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      const body = { answer };
      if (answer === "counter") {
        body.price_month = month === "" ? null : Number(month);
        body.price_night = night === "" ? null : Number(night);
      }
      await axios.post(`${API}/residence-response/${token}`, body);
      setDone(true);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "Tarif invalide ou envoi impossible");
    } finally {
      setBusy(false);
    }
  };

  if (!info) return <Shell><p className="text-sm text-[#666]" data-testid="residence-response-status">{error || "Chargement de la demande…"}</p></Shell>;
  if (done) {
    return (
      <Shell>
        <div className="text-center py-6" data-testid="residence-response-done">
          <CheckCircle2 className="w-10 h-10 text-[#4F6B4A] mx-auto" />
          <h1 className="font-serif text-3xl text-[#1A1A1A] mt-4">Merci, réponse enregistrée</h1>
          <p className="text-sm text-[#666] mt-2">L'équipe matchappart a été prévenue. Vous pouvez modifier votre réponse avec le même lien tant que le client n'a pas finalisé son choix.</p>
        </div>
      </Shell>
    );
  }
  const counterInvalid = answer === "counter" && month === "" && night === "";
  return (
    <Shell>
      <div className="text-xs tracking-[0.25em] uppercase font-semibold text-[#8C6C50]">Demande d'hébergement</div>
      <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#1A1A1A] mt-3" data-testid="residence-response-title">{info.listing.label}</h1>
      {info.listing.address && <p className="text-sm text-[#666] mt-1">{info.listing.address}</p>}
      <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm" data-testid="residence-response-lines">
        {info.lines.map((x) => (
          <div key={x.label}><dt className="text-[#666] text-xs">{x.label}</dt><dd className="text-[#1A1A1A] font-medium">{x.value}</dd></div>
        ))}
        <div><dt className="text-[#666] text-xs">Tarif annoncé (base {info.tier === "3m" ? "3 mois" : "1 mois"})</dt><dd className="text-[#1A1A1A] font-medium"><Announced a={info.announced} /></dd></div>
      </dl>
      {info.closed ? (
        <p className="mt-8 text-sm bg-[#F5EFE6] text-[#6B5B49] rounded-md px-4 py-3" data-testid="residence-response-closed">{info.closed}</p>
      ) : (
        <div className="mt-8">
          {info.current && <p className="text-xs text-[#4F6B4A] mb-2" data-testid="residence-response-current">Vous avez déjà répondu le {new Date(info.current.updated_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} — vous pouvez modifier votre réponse.</p>}
          <div className="font-medium text-[#1A1A1A]">Votre réponse</div>
          <Choices answer={answer} setAnswer={setAnswer} />
          {answer === "counter" && (
            <div className="mt-5 flex flex-col sm:flex-row gap-4" data-testid="residence-counter-fields">
              <PriceInput label="Tarif au mois" value={month} onChange={setMonth} testid="residence-counter-month" />
              <PriceInput label="Tarif à la nuit" value={night} onChange={setNight} testid="residence-counter-night" />
            </div>
          )}
          {error && <p className="mt-4 text-sm text-[#8A3B32]" data-testid="residence-response-error">{error}</p>}
          <button type="button" onClick={submit} disabled={!answer || busy || counterInvalid} className="mt-6 w-full sm:w-auto btn-primary px-8 py-3 rounded-full text-sm font-medium disabled:opacity-50" data-testid="residence-response-submit">
            {busy ? "Envoi…" : "Envoyer ma réponse"}
          </button>
        </div>
      )}
    </Shell>
  );
}
